import React, { PureComponent } from 'react';
import styled from 'styled-components';
import { space, width } from 'styled-system';
import propTypes from '@styled-system/prop-types';
import { rotate360Animation } from '../util/keyframes';
import PropTypes from 'prop-types';

function adjustTextColor(bgColor) {
  bgColor = bgColor.substr(1, 6);
  const bigint = parseInt(bgColor, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? '#000000' : '#ffffff';
}

const RawButton = styled.button`
  ${space};
  ${width};
  box-sizing: border-box;
  min-height: ${props => (props.tertiary ? '30px' : '40px')};
  padding: 0 20px;
  border-radius: 4px;
  border: ${props =>
    props.secondary || props.tertiary
      ? `2px solid ${props.theme.colors.primary}`
      : 'none'};
  background: ${props =>
    props.secondary || props.tertiary || props.transparent
      ? 'none'
      : props.theme.colors.primary};
  color: ${props =>
    props.secondary || props.tertiary || props.transparent
      ? props.theme.colors.primary
      : adjustTextColor(props.theme.colors.primary)};
  cursor: pointer;
  fill: ${props =>
    props.secondary || props.tertiary
      ? props.theme.colors.primary
      : props.theme.colors.aux.white};
  font-family: ${props => props.theme.font}, 'Helvetica Neue', Helvetica,
    sans-serif;
  font-weight: 300;
  font-size: 1em;
  letter-spacing: 0.4px;
  text-align: center;
  outline: none;
  position: relative;
  &:active,
  &:focus,
  &:hover {
    color: ${props => adjustTextColor(props.theme.colors.secondary)};
    border: ${props =>
      props.secondary || props.tertiary
        ? `2px solid ${
            props.loading
              ? props.theme.colors.primary
              : props.theme.colors.secondary
          }`
        : 'none'};
    background: ${props =>
      props.loading
        ? props.theme.colors.primary
        : props.theme.colors.secondary};
    ${props => (props.loading ? 'cursor: progress;' : null)};
  }

  &:disabled {
    background: ${props => props.theme.colors.tertiary};
    color: ${props => props.theme.colors.aux.darkGrey};
    cursor: not-allowed;
    fill: ${props => props.theme.colors.aux.darkGrey};
  }
`;

const TextWrapper = styled.span`
  ${props => (props.loading ? 'visibility: hidden;' : null)};
`;

const LoadingOuter = styled.div`
  background-image: linear-gradient(
    145deg,
    ${props => props.theme.colors.secondary},
    ${props => props.theme.colors.aux.white}
  );
  ${rotate360Animation};
  margin: auto;
  display: flex;
  width: 25px;
  height: 25px;
  position: absolute;
  left: 0;
  right: 0;
  top: 7.5px;
  margin-left: auto;
  margin-right: auto;
  border-radius: 200%;
`;

const LoadingInner = styled.div`
  display: flex;
  margin-left: auto;
  margin-right: auto;
  margin-top: 5px;
  margin-bottom: 5px;
  background: ${props => props.theme.colors.primary};
  width: 15px;
  height: 15px;
  right: 5px;
  position: absolute;
  border-radius: 200%;
`;

class Button extends PureComponent {
  render() {
    const { children, loading, ...props } = this.props;
    return (
      <RawButton {...props}>
        {loading && (
          <LoadingOuter>
            <LoadingInner />
          </LoadingOuter>
        )}
        <TextWrapper loading={loading ? 'true' : undefined}>
          {children}
        </TextWrapper>
      </RawButton>
    );
  }
}

Button.defaultProps = {
  secondary: false,
  tertiary: false,
  transparent: false,
  loading: false,
};

Button.propTypes = {
  /**
   * Enable the secondary button styling
   */
  secondary: PropTypes.bool,
  /**
   * Enable the tertiary button styling
   */
  tertiary: PropTypes.bool,
  /**
   * Enable transparent button styling
   */
  transparent: PropTypes.bool,
  /**
   * Click event handler
   */
  onClick: PropTypes.func,
  /**
   * Enable the loading state
   */
  loading: PropTypes.bool,
  ...propTypes.space,
  ...propTypes.layout,
};

export default Button;
